<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { useBusinessUnitAdminStore, type BusinessUnitItem } from '@/stores/useBusinessUnitAdminStore'
import { useToast } from 'vue-toast-notification'
import { ArrowLeftIcon, PencilSquareIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const store = useBusinessUnitAdminStore()
const $toast = useToast()

const showModal = ref(false)
const editingItem = ref<BusinessUnitItem | null>(null)
const jsonText = ref('')
const jsonError = ref<string | null>(null)
const saving = ref(false)

const BU_LABELS: Record<string, string> = {
  UNIXO_ONE: 'Unixo One',
  UNIXO_CORP: 'Unixo Corp',
  UNIXO_FACTORY: 'Unixo Factory',
  UNIXO_SECURE: 'Unixo Secure',
  UNIXO_NEGOCE: 'Unixo Négoce',
  ODY_C: 'Ody-C',
  OOB: 'OOB',
  GROUP_SOLUTIONS: 'Group Solutions',
  UNIXO_ACR: 'Unixo ACR',
  UNIXO_CUSTOMER_SUCCESS: 'Unixo Customer Success',
}

function buLabel(bu: string): string {
  return BU_LABELS[bu] ?? bu
}

function openEdit(item: BusinessUnitItem) {
  editingItem.value = item
  jsonText.value = JSON.stringify(item.preferences ?? {}, null, 2)
  jsonError.value = null
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editingItem.value = null
  jsonText.value = ''
  jsonError.value = null
}

function validateJson(text: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(text)
    if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

watch(jsonText, (val) => {
  if (!val.trim()) {
    jsonError.value = null
    return
  }
  const parsed = validateJson(val)
  jsonError.value = parsed === null ? 'JSON invalide — attendu un objet {}' : null
})

const canSave = computed(() => {
  if (!jsonText.value.trim()) return true // vide = reset à {}
  return jsonError.value === null
})

async function save() {
  if (!editingItem.value) return
  saving.value = true
  try {
    const preferences = jsonText.value.trim() ? (validateJson(jsonText.value) ?? {}) : {}
    const ok = await store.updatePreferences(editingItem.value.bu, preferences)
    if (ok) {
      $toast.success('Préférences mises à jour')
      closeModal()
    } else {
      $toast.error('Erreur lors de la mise à jour')
    }
  } finally {
    saving.value = false
  }
}

/** Active/désactive le layout V2 (ApyLayout) pour la BU via sa préférence `layoutApy`. */
async function toggleLayoutApy(item: BusinessUnitItem, enabled: boolean) {
  const next = { ...(item.preferences ?? {}), layoutApy: enabled }
  const ok = await store.updatePreferences(item.bu, next)
  if (ok) $toast.success(`Layout V2 ${enabled ? 'activé' : 'désactivé'} — ${buLabel(item.bu)}`)
  else $toast.error('Erreur lors de la mise à jour')
}

function formatJson() {
  const parsed = validateJson(jsonText.value)
  if (parsed) jsonText.value = JSON.stringify(parsed, null, 2)
}

function handleKeydown(event: KeyboardEvent) {
  const textarea = event.target as HTMLTextAreaElement
  const { selectionStart, selectionEnd, value } = textarea

  if (event.key === 'Tab') {
    event.preventDefault()
    const indent = '  '
    jsonText.value = value.substring(0, selectionStart) + indent + value.substring(selectionEnd)
    nextTick(() => {
      textarea.selectionStart = textarea.selectionEnd = selectionStart + indent.length
    })
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1
    const lineContent = value.substring(lineStart, selectionStart)
    const currentIndent = lineContent.match(/^(\s*)/)?.[1] ?? ''
    const charBefore = value[selectionStart - 1]
    const charAfter = value[selectionEnd]

    let newValue: string
    let newCursor: number

    if ((charBefore === '{' && charAfter === '}') || (charBefore === '[' && charAfter === ']')) {
      const extraIndent = currentIndent + '  '
      const insertion = '\n' + extraIndent + '\n' + currentIndent
      newValue = value.substring(0, selectionStart) + insertion + value.substring(selectionEnd)
      newCursor = selectionStart + 1 + extraIndent.length
    } else if (charBefore === '{' || charBefore === '[') {
      const extraIndent = currentIndent + '  '
      const insertion = '\n' + extraIndent
      newValue = value.substring(0, selectionStart) + insertion + value.substring(selectionEnd)
      newCursor = selectionStart + insertion.length
    } else {
      const insertion = '\n' + currentIndent
      newValue = value.substring(0, selectionStart) + insertion + value.substring(selectionEnd)
      newCursor = selectionStart + insertion.length
    }

    jsonText.value = newValue
    nextTick(() => {
      textarea.selectionStart = textarea.selectionEnd = newCursor
    })
  }
}

onMounted(() => store.fetchAll())
</script>

<template>
  <div class="p-6">
    <div class="flex items-center gap-4 mb-4">
      <button class="btn btn-ghost btn-sm" @click="router.push('/dashboard/admin')">
        <ArrowLeftIcon class="w-5 h-5" />
      </button>
      <Bs2Title title="Business Units" subtitle="Administration — préférences par BU" />
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body">
        <h2 class="card-title mb-4">Business Units ({{ store.items.length }})</h2>

        <div v-if="store.isLoading" class="flex justify-center py-12">
          <span class="loading loading-spinner loading-lg" />
        </div>

        <div v-else class="overflow-x-auto">
          <table class="table table-zebra w-full">
            <thead>
              <tr>
                <th>BU</th>
                <th>Identifiant</th>
                <th>Layout V2</th>
                <th>Préférences</th>
                <th class="w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in store.items" :key="item.bu">
                <td class="font-semibold">{{ buLabel(item.bu) }}</td>
                <td>
                  <span class="badge badge-ghost font-mono text-xs">{{ item.bu }}</span>
                </td>
                <td>
                  <input
                    type="checkbox"
                    class="toggle toggle-sm toggle-primary"
                    :checked="item.preferences?.layoutApy === true"
                    :aria-label="`Activer le layout V2 pour ${buLabel(item.bu)}`"
                    @change="toggleLayoutApy(item, ($event.target as HTMLInputElement).checked)"
                  />
                </td>
                <td>
                  <span v-if="!item.preferences || !Object.keys(item.preferences).length" class="text-base-content/40 italic text-sm">
                    Aucune préférence
                  </span>
                  <code v-else class="text-xs bg-base-200 px-2 py-1 rounded block max-w-xs truncate">
                    {{ JSON.stringify(item.preferences) }}
                  </code>
                </td>
                <td>
                  <ButtonDaisy
                    text="Modifier"
                    variant="ghost"
                    size="xs"
                    :icon="PencilSquareIcon"
                    @click="openEdit(item)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Modale édition préférences -->
  <div class="modal" :class="{ 'modal-open': showModal }">
    <div class="modal-box w-11/12 max-w-2xl">
      <h3 class="font-bold text-lg">
        Préférences — {{ editingItem ? buLabel(editingItem.bu) : '' }}
        <span class="badge badge-ghost font-mono text-xs ml-2">{{ editingItem?.bu }}</span>
      </h3>
      <p class="text-sm text-base-content/60 mt-1">
        Le nom de la BU est basé sur l'enum et n'est pas modifiable.
      </p>

      <div class="mt-4 space-y-2">
        <div class="flex items-center justify-between">
          <label class="label-text font-medium">Préférences (JSON)</label>
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            :disabled="!!jsonError || !jsonText.trim()"
            @click="formatJson"
          >
            Formater
          </button>
        </div>
        <textarea
          v-model="jsonText"
          class="textarea textarea-bordered w-full font-mono text-sm h-64 resize-y"
          :class="{ 'textarea-error': jsonError }"
          placeholder="{}"
          spellcheck="false"
          @keydown="handleKeydown"
        />
        <p v-if="jsonError" class="text-error text-xs flex items-center gap-1">
          <span>⚠</span> {{ jsonError }}
        </p>
        <p v-else class="text-base-content/40 text-xs">
          Objet JSON uniquement. Laisser vide pour réinitialiser à <code>{}</code>.
        </p>
      </div>

      <div class="modal-action">
        <button class="btn" @click="closeModal">Annuler</button>
        <button
          class="btn btn-primary"
          :disabled="saving || !canSave"
          @click="save"
        >
          {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
        </button>
      </div>
    </div>
    <div class="modal-backdrop" @click="closeModal" />
  </div>
</template>
